import { Select, InputBase, styled } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { BackgroundTypes, DEFAULT_INPUT_HEIGHT, Themes } from '../../constants';
import { FORTA_WHITE, FORTA_BLACK, FORTA_ORANGE } from '../../utils/colors';
import { getThemeColor } from '../../utils/common';

import type { SelectProps as MSelectProps, InputBaseProps as MInputBaseProps } from '@mui/material';
import type { ReactNode } from 'react';

interface MultiSelectProps extends MSelectProps {
  value: number[];
  colorTheme?: Themes;
  backgroundType?: BackgroundTypes;
  error?: boolean;
  helperText?: ReactNode;
  inputHeight?: number;
  popoverWidth?: number;
  autoHeight?: boolean;
}

interface InputBaseProps extends MInputBaseProps {
  colorTheme?: Themes;
  coloredBackground?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  inputHeight?: number;
  autoHeight?: boolean;
}

const StyledSelect = styled(InputBase, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'error' &&
    prop !== 'inputHeight' &&
    prop !== 'autoHeight',
})<InputBaseProps>(({ theme, colorTheme, coloredBackground, error, inputHeight, autoHeight }) => ({
  position: 'relative',
  height: autoHeight ? 'fit-content' : inputHeight,

  '& .MuiSelect-select.MuiInputBase-input': {
    position: 'relative',
    backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
    boxSizing: 'border-box',
    color: FORTA_BLACK,
    boxShadow: 'none',
    borderRadius: 10,
    padding: '0 2em 0 1em',
    minWidth: 150,
    height: '100%',
    fontSize: 16,
    border: error ? `3px solid ${FORTA_ORANGE}` : 'none',
    textAlign: 'left',

    '&, & > *': {
      textOverflow: autoHeight ? 'none' : 'ellipsis',
      whiteSpace: autoHeight ? 'normal' : 'no-wrap',
      overflow: 'hidden',
      lineHeight: autoHeight ? '1.5em' : `${inputHeight}px`,
      minHeight: autoHeight ? 40 : 0,
      margin: autoHeight ? '10px 0' : '0',
    },

    '&:focus': {
      borderRadius: 10,
    },
    '&.Mui-disabled': {
      WebkitTextFillColor: FORTA_BLACK,
    },
  },
  '& .MuiSvgIcon-root': {
    position: 'absolute',
    right: theme.spacing(1),
    color: getThemeColor(colorTheme).primary,
  },
  '&.Mui-disabled .MuiSvgIcon-root': {
    color: getThemeColor(colorTheme).light2,
  },
}));

const FMultiSelect = (props: MultiSelectProps) => {
  const {
    children,
    colorTheme = Themes.Primary,
    backgroundType = BackgroundTypes.White,
    inputHeight = DEFAULT_INPUT_HEIGHT,
    popoverWidth,
    error = false,
    autoHeight = false,
  } = props;

  const MenuProps = {
    sx: {
      '& .MuiPaper-root': {
        padding: '10px 0 10px 0',
        borderRadius: '8px',
        backgroundColor:
          backgroundType !== BackgroundTypes.White
            ? getThemeColor(colorTheme).light3
            : FORTA_WHITE,
        width: popoverWidth ? popoverWidth : 'auto',
      },
      '& .MuiMenuItem-root': {
        fontSize: 16,
        whiteSpace: 'normal',

        '&:nth-of-type(even)':
          backgroundType === BackgroundTypes.EvenColored
            ? {
                backgroundColor: FORTA_WHITE,
              }
            : {},
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'transparent',
      },
    },
    MenuListProps: {
      style: {
        padding: 0,
      },
    },
  };

  return (
    <Select
      {...props}
      multiple
      IconComponent={KeyboardArrowDownIcon}
      input={
        <StyledSelect
          autoHeight={autoHeight}
          colorTheme={colorTheme}
          coloredBackground={backgroundType !== BackgroundTypes.White}
          error={error}
          inputHeight={inputHeight}
        />
      }
      MenuProps={MenuProps}
    >
      {children}
    </Select>
  );
};

export default FMultiSelect;
